import { z } from "zod";
/**
 * The platforms a device row may claim.
 *
 * One member today, and it is not an empty promise — the same call
 * `requestOtpInput` and `NotificationChannel` already made in this repository:
 * an enum member with no delivery behind it is a promise the API cannot keep
 * [source: `packages/infra/mailer/src/notification-provider.ts:1-17`]. The
 * union widens the day there is a second sender, and `registerDeviceInput`
 * below widens with it.
 *
 * It exists at all — rather than the column simply being absent — because a
 * registration token's MEANING is platform-specific. A stored token with no
 * platform beside it is a row that can never hold an iOS token without a
 * migration, and the sender could not tell the two apart if it did.
 */
export const DEVICE_PLATFORMS = ["android"];
/**
 * `POST /me/devices` — the app hands the server the token it just obtained.
 *
 * `strictObject`, so an unexpected field is REFUSED rather than stripped. A
 * client sending `{ token, platform, deviceName }` should learn that the server
 * does not store a device name, not have it quietly dropped and then wonder
 * where it went.
 *
 * ── `token` IS THE NATIVE FCM REGISTRATION TOKEN ────────────────────────────
 * The `.data` of `getDevicePushTokenAsync()`, never the whole
 * `{ type, data }` object it returns — passing that object across is the
 * classic slip, and it parses as an object where a string is expected only
 * because nothing here would otherwise look. Never
 * `getExpoPushTokenAsync()`: that mints an Expo token for Expo's own relay,
 * which is a different service and a different string.
 *
 * The bound is 4096 because FCM registration tokens run well over 150
 * characters and the format is not documented as bounded. Generous and finite
 * beats unbounded: `z.string()` with no `.max()` is a database column with no
 * ceiling, reachable by anyone with an access token.
 *
 * There is no account id in this shape by design. The account comes from the
 * verified principal on the request; a route that accepted a target account
 * would let anyone register a device against anyone else's notifications.
 */
export const registerDeviceInput = z.strictObject({
    /** The NATIVE FCM registration token — the `.data` of getDevicePushTokenAsync(). */
    token: z.string().min(1).max(4096),
    platform: z.literal("android"),
});
/**
 * Registration answers `204` with no body, so there is nothing to parse.
 *
 * Declared anyway, and named like every other output in this package, so a
 * client generated or written against the contract has one shape to reach for
 * per route rather than a special case it has to know about. It also gives the
 * next person somewhere to put a field, on the day registration starts
 * answering something.
 */
export const registerDeviceOutput = z.void();
/**
 * `DELETE /me/devices` — the app forgets its token, and so does the server.
 *
 * ── IT TAKES THE TOKEN, NOT A DEVICE ID ─────────────────────────────────────
 * The client knows exactly one thing about its own registration: the string it
 * was given by FCM. Answering registration with an id and requiring it back
 * here would mint a SECOND identifier for one device — one the client has to
 * persist, one that can be lost while the token survives, and one that makes
 * "forget this device" fail for a client that has the token in hand.
 *
 * Deleting an unknown token succeeds. A client retrying after a dropped
 * response must not get an error for having succeeded the first time.
 */
export const unregisterDeviceInput = z.strictObject({
    token: z.string().min(1).max(4096),
});
//# sourceMappingURL=devices.js.map